import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Bell, StickyNote, UserPlus, IndianRupee, ClipboardList, X, CheckCheck, HelpCircle, MessageCircle } from "lucide-react";

const KIND_ICON = { note: StickyNote, lead: UserPlus, closing: IndianRupee, order: ClipboardList, question: HelpCircle, answer: MessageCircle, info: Bell };
const KIND_COLOR = {
  note: "bg-violet-100 text-violet-700",
  lead: "bg-blue-100 text-blue-700",
  closing: "bg-emerald-100 text-emerald-700",
  order: "bg-indigo-100 text-indigo-700",
  question: "bg-rose-100 text-rose-700",
  answer: "bg-emerald-100 text-emerald-700",
  info: "bg-slate-100 text-slate-600",
};

const relTime = (iso) => {
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const prevNewest = useRef(null);
  const initialized = useRef(false);

  const playSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [880, 1174.7].forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        o.connect(g);
        g.connect(ctx.destination);
        const t = now + i * 0.15;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
        o.start(t);
        o.stop(t + 0.34);
      });
      setTimeout(() => ctx.close(), 900);
    } catch {
      /* ignore */
    }
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/activities`);
      const data = r.data?.items || [];
      setItems(data);
      setUnread(r.data?.unread || 0);
      const newest = data[0]?.created_at || null;
      const isNewer = newest && (!prevNewest.current || newest > prevNewest.current);
      if (initialized.current && isNewer && (r.data?.unread || 0) > 0) playSound();
      if (isNewer) prevNewest.current = newest;
      initialized.current = true;
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    setItems((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.post(`${API}/activities/${id}/read`);
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((a) => ({ ...a, read: true })));
    setUnread(0);
    try {
      await axios.post(`${API}/activities/read-all`);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50"
        style={{ transition: "background-color 150ms ease" }}
        title="Updates"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-h-[440px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
            <span className="font-heading text-sm font-bold text-slate-900">
              Updates {unread > 0 && <span className="text-rose-500">· {unread} new</span>}
            </span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all"
                  onClick={markAllRead}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700"
                >
                  <CheckCheck size={13} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X size={15} />
              </button>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">No updates yet</div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {items.map((a) => {
                const Icon = KIND_ICON[a.kind] || Bell;
                return (
                  <li
                    key={a.id}
                    data-testid="notif-item"
                    onClick={() => !a.read && markRead(a.id)}
                    className={`flex gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${
                      !a.read ? "bg-blue-50/50" : ""
                    }`}
                    style={{ transition: "background-color 150ms ease" }}
                  >
                    <span
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        KIND_COLOR[a.kind] || KIND_COLOR.info
                      }`}
                    >
                      <Icon size={15} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-700 leading-snug">
                        <span className="font-semibold text-slate-900">{a.actor}</span> {a.message}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{relTime(a.created_at)}</div>
                    </div>
                    {!a.read && (
                      <span
                        data-testid={`notif-unread-dot-${a.id}`}
                        className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1.5"
                        title="Unread"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
